import { Subject } from "rxjs";
import { Cellstyle, NumericCellChangeArg } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { VKNumericHotCell } from "~app/features/virkeskop/models/hotdoc/vk-numeric.hot-cell";
import { TradslagItem, VKForkalkylTradslagHDCellStyles } from "~app/features/virkeskop/models/tradslag/tradslag-models";

const parseFloatWithFormat = (value: string) =>
    parseFloat(value.replace(",", "."));

export type TradslagColumnDefinition<T extends TradslagItem> = IHotColumnDefinition & {
    sumFunction?: (values: number[], tableData: T[]) => number;
    tooltip?: string;
    disabled?: (item: TradslagItem) => boolean;
    validateFn?: (value: any) => boolean;
};

export class TradslagTableBinder<T extends TradslagItem> {

    hotDoc: HotDoc;
    cellStyles: Map<string, VKForkalkylTradslagHDCellStyles>;

    private readonly modelChangeSrc = new Subject<void>();
    get modelChange$() {
        return this.modelChangeSrc.asObservable();
    }

    constructor(
        private readonly columns: TradslagColumnDefinition<T>[]) {}

    rebind() {
        this.hotDoc.rebind();
    }

    initTable(tableData: T[]): void {
        const cols = [
            {
                header: "Trädslag",
                cellName:"tradslag",
                readOnly: true,
                type: "text"
            },
            ...this.columns,
            {
                header:"",
                cellName:"remove-row",
                readOnly:true
            }
        ];

        cols.forEach(c => {
            this.createHeaderWithTitle(c);
        });

        const colFactory = () => cols;

        const rowFactory = (doc: HotDoc) => this.rowFactory(doc, tableData);

        const settings: IHotDocSettings = {
            raskSettings: {
                calculateSum: false,
                numericParser: parseFloatWithFormat
            },
            stretchH: "all"
        };
        this.hotDoc = new HotDoc(colFactory, rowFactory, () => settings);
    }

    setCellStyles(inCellStyles: Map<string, VKForkalkylTradslagHDCellStyles>) {
        this.cellStyles = inCellStyles;
        this.rebind();
    }

    private createHeaderWithTitle(col: TradslagColumnDefinition<T>) {
        if(col.tooltip) {
            col.header = `<span title='${col.tooltip}'>${col.header}</span>`;
        }
    }

    private createCell(row: HotRow, item: T, col: TradslagColumnDefinition<T>, customCellStyle: Cellstyle, validateFn?: (value: any) => boolean) {
        const callback = (change: NumericCellChangeArg) => {
            item[col.cellName] = change.newValue;
            this.rebind();
            change.complete();
            this.modelChangeSrc.next();
        };

        let cellStyle = Cellstyle.Default;
        if(col.disabled && col.disabled(item)) {
            cellStyle = Cellstyle.ReadOnlyInvisibleText;
        } else if (customCellStyle) {
            cellStyle = customCellStyle;
        }

        const formatOptions = { maximumFractionDigits: 20 };
        row.add(col.cellName, new VKNumericHotCell(
            item[col.cellName], [], cellStyle, callback, formatOptions, validateFn));
    }

    private itemRowFactory(tableData: T[], item: T, row: HotRow) {
        const rowCellStyles = this.cellStyles?.get(item.tradslag.namn);
        row.addText("tradslag", item.tradslag.namn, rowCellStyles?.tradslag);

        this.columns.forEach(c => {
            const cellStyle = rowCellStyles?.[c.cellName];
            this.createCell(row, item, c, cellStyle, c.validateFn);
        });

        const instance = row.addButton("remove-row");
        instance.iconClass = "fa-regular fa-trash-can text-danger";
        instance.onClickCallback = () => {
            const index = tableData.indexOf(item);
            if(index > -1) {
                tableData.splice(index, 1);
                this.modelChangeSrc.next();
                this.rebind();
            }
        };
    }

    private summaRowFactory(tableData: T[], row: HotRow) {
        row.addText("tradslag", "Summa");

        for(const column of this.columns) {
            if(!column.sumFunction) {
                continue;
            }

            const values = tableData.map(item => item[column.cellName] as number);
            const sum = column.sumFunction(values, tableData);
            const cellstyle = Cellstyle.Readonly;

            row.add(column.cellName, new VKNumericHotCell(sum, [], cellstyle));
        }
    }

    private rowFactory(doc: HotDoc, tableData: T[]) {
        const sortedTableData = [...tableData]
            .sort((a,b) => a.tradslag.sekvensnummer - b.tradslag.sekvensnummer);

        sortedTableData
            .forEach(item => {
                doc.createRow(row => this.itemRowFactory(tableData, item, row));
            });

        // summa rad
        doc.createRow(row => this.summaRowFactory(tableData, row));
    }
}
